import { SERVER } from "../requestor/config";

const PROBE_TIMEOUT_MS = 2000;
const READY_MESSAGE = "versiond-download-ready";
const START_MESSAGE = "versiond-download-start";

type DownloadMessage = {
  type: string;
  token: string;
};

export type DownloadRuntime = {
  openerOrigin: string;
  open: (url: string) => Window | null;
  navigateCurrent: (url: string) => void;
  navigateWindow: (target: Window, url: string) => void;
  addMessageListener: (listener: (event: MessageEvent) => void) => void;
  removeMessageListener: (listener: (event: MessageEvent) => void) => void;
  setTimer: (callback: () => void, delay: number) => number;
  clearTimer: (timerID: number) => void;
  randomUUID: () => string;
  postMessage: (
    target: Window,
    message: DownloadMessage,
    targetOrigin: string,
  ) => void;
};

function normalizeOrigin(raw: string | undefined): string | null {
  if (!raw) {
    return null;
  }

  try {
    const parsed = new URL(raw);
    if (
      (parsed.protocol !== "http:" && parsed.protocol !== "https:") ||
      parsed.username !== "" ||
      parsed.password !== "" ||
      parsed.pathname !== "/" ||
      parsed.search !== "" ||
      parsed.hash !== ""
    ) {
      return null;
    }
    return parsed.origin;
  } catch {
    return null;
  }
}

function fileURL(origin: string, fileName: string): string {
  return `${origin}/v1/files/${encodeURIComponent(fileName)}`;
}

const browserRuntime: DownloadRuntime = {
  openerOrigin: window.location.origin,
  open: (url) => window.open(url, "_blank"),
  navigateCurrent: (url) => {
    window.location.href = url;
  },
  navigateWindow: (target, url) => {
    target.location.href = url;
  },
  addMessageListener: (listener) => window.addEventListener("message", listener),
  removeMessageListener: (listener) => window.removeEventListener("message", listener),
  setTimer: (callback, delay) => window.setTimeout(callback, delay),
  clearTimer: (timerID) => window.clearTimeout(timerID),
  randomUUID: () => window.crypto.randomUUID(),
  postMessage: (target, message, targetOrigin) => {
    target.postMessage(message, targetOrigin);
  },
};

export class DownloadCoordinator {
  private readonly publicOrigin: string;
  private readonly internalOrigin: string | null;

  constructor(
    publicServer: string,
    internalServer: string | undefined,
    private readonly runtime: DownloadRuntime = browserRuntime,
  ) {
    const normalizedPublicOrigin = normalizeOrigin(publicServer);
    if (!normalizedPublicOrigin) {
      throw new Error("Invalid public VersionD server origin");
    }

    this.publicOrigin = normalizedPublicOrigin;
    this.internalOrigin = normalizeOrigin(internalServer);
  }

  publicUrl(fileName: string): string {
    return fileURL(this.publicOrigin, fileName);
  }

  start(fileName: string): void {
    const publicUrl = this.publicUrl(fileName);
    if (!this.internalOrigin) {
      this.runtime.navigateCurrent(publicUrl);
      return;
    }

    const token = this.runtime.randomUUID();
    const probeURL = new URL(
      `/v1/download/probe/${encodeURIComponent(fileName)}`,
      this.internalOrigin,
    );
    probeURL.searchParams.set("token", token);
    probeURL.searchParams.set("openerOrigin", this.runtime.openerOrigin);

    const probeWindow = this.runtime.open(probeURL.toString());
    if (!probeWindow) {
      this.runtime.navigateCurrent(publicUrl);
      return;
    }

    let settled = false;
    let timerID = 0;

    const cleanup = () => {
      this.runtime.clearTimer(timerID);
      this.runtime.removeMessageListener(onMessage);
    };

    const navigatePublic = () => {
      if (probeWindow.closed) {
        this.runtime.navigateCurrent(publicUrl);
        return;
      }

      try {
        this.runtime.navigateWindow(probeWindow, publicUrl);
      } catch {
        this.runtime.navigateCurrent(publicUrl);
      }
    };

    const selectPublic = () => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      navigatePublic();
    };

    const onMessage = (event: MessageEvent) => {
      if (
        settled ||
        event.source !== probeWindow ||
        event.origin !== this.internalOrigin ||
        !event.data ||
        event.data.type !== READY_MESSAGE ||
        event.data.token !== token
      ) {
        return;
      }

      settled = true;
      cleanup();
      try {
        this.runtime.postMessage(
          probeWindow,
          { type: START_MESSAGE, token },
          this.internalOrigin,
        );
      } catch {
        navigatePublic();
      }
    };

    this.runtime.addMessageListener(onMessage);
    timerID = this.runtime.setTimer(selectPublic, PROBE_TIMEOUT_MS);
  }
}

export const downloadCoordinator = new DownloadCoordinator(
  SERVER,
  process.env.REACT_APP_INTERNAL_SERVER,
);
